import { useEffect, useRef, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import useSWR from "swr";

import { signOut } from "../auth";
import { buildNav, isWithinSection, type NavEntry } from "../config/nav";
import { useSupportReplies } from "../hooks/useSupportReplies";
import { api, type Me, type PendingApprovalRow } from "../services/api";

import { Avatar } from "./Avatar";
import { Icon } from "./Icon";

/** The pending-approvals count, rendered as a small critical badge. */
function PendingBadge({ count }: { count: number }) {
  return (
    <span
      className="badge badge-critical"
      style={{ marginLeft: 8 }}
      aria-label={`${count} pending approvals`}
    >
      {count}
    </span>
  );
}

export function Sidebar({
  isOpen = false,
  onNavigate,
}: {
  isOpen?: boolean;
  onNavigate?: () => void;
} = {}) {
  const { data: me } = useSWR<Me>("/api/me", () => api.me());
  const { data: pending } = useSWR<PendingApprovalRow[]>(
    me ? "/api/approvals/pending" : null,
    () => api.approvals.listPending(),
  );
  const pendingCount = pending?.length ?? 0;
  const { count: supportReplies, label: supportReplyLabel } = useSupportReplies(me);

  const navGroups = buildNav(me);

  // Expandable section groups (Service Hub / Knowledge Base / Admin). The group
  // for the section you're in auto-expands; the chevron toggles any group open
  // or closed. Overrides are cleared on navigation so the active group always
  // opens and a previously-toggled one doesn't linger.
  const { pathname } = useLocation();
  const [openOverrides, setOpenOverrides] = useState<Record<string, boolean>>({});
  useEffect(() => {
    setOpenOverrides({});
  }, [pathname]);
  const isGroupOpen = (item: NavEntry) =>
    openOverrides[item.to] ?? isWithinSection(pathname, item.to);
  const toggleGroup = (item: NavEntry) =>
    setOpenOverrides((o) => ({
      ...o,
      [item.to]: !(o[item.to] ?? isWithinSection(pathname, item.to)),
    }));

  // Click-to-open account menu (folds Preferences + Delegation + Sign out into
  // the user footer, Facebook-style).
  const [menuOpen, setMenuOpen] = useState(false);
  const footerRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!menuOpen) return;
    function onDown(e: MouseEvent) {
      if (footerRef.current && !footerRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setMenuOpen(false);
    }
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);

  const delegationEnabled = me?.approval_delegation_enabled !== false;
  const closeMenu = () => setMenuOpen(false);

  return (
    <aside className={`sidebar${isOpen ? " sidebar-open" : ""}`}>
      <div className="sidebar-header">
        <img
          src={me?.logo_url || "/iquest-logo-light.svg"}
          alt={me?.company_name || "iQuest"}
          className="sidebar-logo"
        />
      </div>
      {/* A tap on any nav link closes the mobile drawer (no-op on desktop). */}
      <nav className="sidebar-nav" onClick={onNavigate}>
        {navGroups.map((group) => (
          <div key={group.heading}>
            {/* The heading only earns its keep when it distinguishes one group
                from another; with a single group spanning the whole nav it just
                labels the obvious ("the whole thing is a workspace"), so hide
                it (issue #46). It returns automatically if the nav ever splits
                back into multiple headed groups. */}
            {navGroups.length > 1 && (
              <div className="nav-section-label">{group.heading}</div>
            )}
            {group.items.map((item) =>
              item.children && item.children.length > 0 ? (
                <div className="nav-group" key={item.to}>
                  <div className="nav-group-row">
                    {/* Clicking the label navigates to the section index; the
                        chevron toggles the sub-items without navigating. */}
                    <NavLink
                      to={item.to}
                      end={item.end}
                      className={({ isActive }) =>
                        "nav-item nav-group-link" + (isActive ? " active" : "")
                      }
                    >
                      <span>{item.label}</span>
                      {/* When collapsed, the pending count bubbles up to the
                          parent so it's never hidden inside a closed group. */}
                      {item.trackPending &&
                        pendingCount > 0 &&
                        !isGroupOpen(item) && <PendingBadge count={pendingCount} />}
                    </NavLink>
                    <button
                      type="button"
                      className={
                        "nav-group-toggle" + (isGroupOpen(item) ? " open" : "")
                      }
                      aria-label={`${isGroupOpen(item) ? "Collapse" : "Expand"} ${item.label}`}
                      aria-expanded={isGroupOpen(item)}
                      onClick={(e) => {
                        // Don't navigate or close the mobile drawer on a toggle.
                        e.stopPropagation();
                        toggleGroup(item);
                      }}
                    >
                      <Icon name="chevDown" size={15} />
                    </button>
                  </div>
                  {isGroupOpen(item) && (
                    <div className="nav-subitems">
                      {item.children.map((child) => (
                        <NavLink
                          key={child.to}
                          to={child.to}
                          end={child.end}
                          className={({ isActive }) =>
                            "nav-item nav-subitem" + (isActive ? " active" : "")
                          }
                        >
                          <span>{child.label}</span>
                          {child.trackPending && pendingCount > 0 && (
                            <PendingBadge count={pendingCount} />
                          )}
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) => (isActive ? "nav-item active" : "nav-item")}
                >
                  <span>{item.label}</span>
                  {item.trackPending && pendingCount > 0 && (
                    <PendingBadge count={pendingCount} />
                  )}
                </NavLink>
              ),
            )}
          </div>
        ))}
        {/* Preferences, Delegation and Sign out now live in the account menu on
            the user footer below. */}
      </nav>

      <div className="sidebar-footer" ref={footerRef}>
        {menuOpen && me && (
          <div className="usermenu" role="menu" onClick={(e) => e.stopPropagation()}>
            <div className="usermenu-head">
              <Avatar name={me.display_name} />
              <div style={{ minWidth: 0 }}>
                <div className="usermenu-name">{me.display_name}</div>
                <div className="usermenu-meta">{me.email}</div>
              </div>
            </div>
            <div className="usermenu-list">
              <NavLink
                to="/me/preferences"
                role="menuitem"
                className={({ isActive }) => "usermenu-item" + (isActive ? " active" : "")}
                onClick={closeMenu}
              >
                <Icon name="settings" size={17} />
                <span>Settings</span>
                {supportReplies > 0 && (
                  <span className="badge badge-open usermenu-badge">{supportReplyLabel}</span>
                )}
              </NavLink>
              {delegationEnabled && (
                <NavLink
                  to="/services/delegation"
                  role="menuitem"
                  className={({ isActive }) => "usermenu-item" + (isActive ? " active" : "")}
                  onClick={closeMenu}
                >
                  <Icon name="arrowRight" size={17} />
                  <span>Delegation</span>
                </NavLink>
              )}
            </div>
            <div className="usermenu-sep" />
            <button type="button" role="menuitem" className="usermenu-item" onClick={() => void signOut()}>
              <Icon name="arrowLeft" size={17} />
              <span>Sign out</span>
            </button>
          </div>
        )}

        <button
          type="button"
          className={"sidebar-user-btn" + (menuOpen ? " active" : "")}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((o) => !o)}
          title={supportReplies > 0 ? `${supportReplyLabel} from iQuest in Settings` : undefined}
        >
          <span className="su-avatar">
            <Avatar name={me?.display_name ?? "?"} size="sm" />
            {supportReplies > 0 && <span className="reply-dot" aria-hidden="true" />}
          </span>
          <span className="su-text">
            <span className="su-name">{me?.display_name ?? "…"}</span>
            <span className="su-email">{me?.email ?? ""}</span>
          </span>
          {supportReplies > 0 && (
            <span className="sr-only">{supportReplyLabel} from iQuest</span>
          )}
          <Icon name="chevDown" size={15} className="su-chev" />
        </button>
      </div>
    </aside>
  );
}
